import { FC, PropsWithChildren, useLayoutEffect } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  useFetchStatisticsSoundOptionsQuery,
  useGetV1SettingsQuery
} from '../api/backendApi';
import { useAppSelector } from '../hooks/redux';
import { received as soundOptionsReceived } from '../store/soundOptions';

type PreloadProviderProps = PropsWithChildren;

export const PreloadProvider: FC<PreloadProviderProps> = ({ children }) => {
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const dispatch = useDispatch();
  const {
    data: soundOptions,
    isLoading: isLoadingSoundOptions,
    isUninitialized
  } = useFetchStatisticsSoundOptionsQuery(
    {
      includesExtras: true
    },
    {
      skip: !isAuthenticated,
      selectFromResult: data => {
        return { ...data, data: data.data?.sound_options };
      }
    }
  );

  // Loaded before rendering so that `useTimeZone` never returns a fallback
  // while the user's time zone is still loading.
  const {
    isLoading: isLoadingSettings,
    isUninitialized: isSettingsUninitialized
  } = useGetV1SettingsQuery(undefined, { skip: !isAuthenticated });

  useLayoutEffect(() => {
    if (soundOptions) {
      dispatch(soundOptionsReceived(soundOptions));
    }
  }, [dispatch, isLoadingSoundOptions, soundOptions]);

  const isLoading =
    isLoadingSoundOptions ||
    isLoadingSettings ||
    (isAuthenticated && (isUninitialized || isSettingsUninitialized));

  if (isLoading) {
    return <Trans i18nKey="loading" />;
  }

  return <div>{children}</div>;
};
